const MATHJAX_SKIP_TAGS = new Set([
  'script',
  'noscript',
  'style',
  'textarea',
  'pre',
  'code',
  'math'
]);

const MATH_CONTENT_PATTERN = /(\$\$[\s\S]*?\$\$|\$(?!\$)[\s\S]*?\$|\\\[[\s\S]*?\\\]|\\\([\s\S]*?\\\))/g;

function repairEmphasisToken(token) {
  return String(token || '')
    .replace(/<em>\s*(?=\})/gi, '*')
    .replace(/<\/em>\s*(?=\})/gi, '*')
    .replace(/<em>\s*(?=\{)/gi, '_')
    .replace(/<\/em>\s*(?=\{)/gi, '_')
    // Subscript tags that sit right after a TeX identifier, e.g.
    // `\mathbf{h}<em>k}` or `x</em>{k-1}`. The legacy rich-text editor turned
    // the surrounding `_` markers into an <em> pair; if only one side of the
    // pair touches a brace the tag above misses it, and the leftover element
    // splits the text node so MathJax can no longer pair the delimiters.
    .replace(/([A-Za-z0-9}])<em>/gi, '$1_')
    .replace(/<\/em>(?=[A-Za-z0-9{])/gi, '_');
}

// Some legacy rich-text content interpreted LaTeX "*" and "_" markers as
// emphasis tags. Repair only those tags that sit inside a math delimiter and
// immediately precede a brace, leaving intentional prose emphasis untouched.
export function repairMathEmphasisMarkupInHtml(html) {
  const source = String(html || '');
  if (!source || !/<\/?em>/i.test(source)) {
    return source;
  }

  return source.replace(MATH_CONTENT_PATTERN, repairEmphasisToken);
}

function isEscaped(text, index) {
  let slashCount = 0;
  for (let cursor = index - 1; cursor >= 0 && text[cursor] === '\\'; cursor--) {
    slashCount++;
  }
  return slashCount % 2 === 1;
}
function findNextDollar(text, startIndex) {
  for (let index = startIndex; index < text.length; index++) {
    if (text[index] === '$' && !isEscaped(text, index)) {
      return index;
    }
  }
  return -1;
}

// A dollar delimiter followed by a number is ambiguous: it may be a currency
// amount or a formula such as $5 + 2$. Keep clear numeric formulas intact and
// treat prose-like numeric content as currency.
function isLikelyNumericInlineMath(body) {
  const value = String(body || '').trim();
  if (!value || !/^\d/.test(value)) {
    return false;
  }

  if (/\\[a-z]+/i.test(value)) {
    return true;
  }

  // Pricing prose can contain arithmetic separators, for example
  // "$12 base price + $4.99 delivery fee". Do not let the plus sign alone
  // turn an English description into a MathJax expression.
  if (/[a-z]{2,}/i.test(value)) {
    return false;
  }

  if (/[=+*^_{}]/.test(value)) {
    return true;
  }

  if (/^\d+(?:\.\d+)?[a-z](?:\d+)?$/i.test(value)) {
    return true;
  }

  // Pure decimals such as $0.01$ are common inline math. Without this branch
  // the opening dollar is treated as currency and left as literal text, which
  // both shows raw dollars on the page and desynchronizes the integrity check.
  if (/^\d+(?:\.\d+)+$/.test(value)) {
    return true;
  }

  return value.length <= 40 &&
    /^[\d\s.,()+\-*/]+$/.test(value) &&
    /[+\-*/()]/.test(value);
}

function isCurrencyDollarAt(text, index) {
  const source = String(text || '');
  if (
    source[index] !== '$' ||
    isEscaped(source, index) ||
    !/\d/.test(source[index + 1] || '')
  ) {
    return false;
  }

  const closingIndex = findNextDollar(source, index + 1);
  if (closingIndex === -1) {
    return true;
  }

  return !isLikelyNumericInlineMath(
    source.slice(index + 1, closingIndex)
  );
}

function protectCurrencyDollarsInText(text) {
  const source = String(text || '');
  let result = '';

  for (let index = 0; index < source.length; index++) {
    const current = source[index];
    if (!isCurrencyDollarAt(source, index)) {
      result += current;
      continue;
    }

    result += '<span class="tex2jax_ignore exam-currency-dollar">$</span>';
  }

  return result;
}

function getTagName(tagToken) {
  const match = String(tagToken || '').match(/^<\/?\s*([a-z0-9-]+)/i);
  return match ? match[1].toLowerCase() : '';
}

export function protectCurrencyDollarsInHtml(html) {
  const source = String(html || '');
  if (!source || source.indexOf('$') === -1) {
    return source;
  }

  const tokens = source.split(/(<!--[\s\S]*?-->|<![^>]*>|<[^>]+>)/g);
  const skippedTags = [];

  return tokens.map((token) => {
    if (!token || token[0] !== '<') {
      return skippedTags.length > 0
        ? token
        : protectCurrencyDollarsInText(token);
    }

    const tagName = getTagName(token);
    if (!tagName) {
      return token;
    }

    if (/^<\//.test(token)) {
      const lastIndex = skippedTags.lastIndexOf(tagName);
      if (lastIndex !== -1) {
        skippedTags.splice(lastIndex, 1);
      }
    } else if (
      !/\/>$/.test(token) &&
      (
        MATHJAX_SKIP_TAGS.has(tagName) ||
        /\b(?:tex2jax_ignore|exam-currency-dollar)\b/i.test(token)
      )
    ) {
      skippedTags.push(tagName);
    }

    return token;
  }).join('');
}

function stripSingleDollarMath(text) {
  const source = String(text || '');
  let result = '';

  for (let index = 0; index < source.length; index++) {
    if (
      source[index] !== '$' ||
      isEscaped(source, index) ||
      isCurrencyDollarAt(source, index)
    ) {
      result += source[index];
      continue;
    }

    let closingIndex = -1;
    for (let cursor = index + 1; cursor < source.length; cursor++) {
      if (
        source[cursor] === '$' &&
        !isEscaped(source, cursor) &&
        !isCurrencyDollarAt(source, cursor)
      ) {
        closingIndex = cursor;
        break;
      }
    }

    if (closingIndex === -1) {
      result += source[index];
      continue;
    }

    result += ' ';
    index = closingIndex;
  }

  return result;
}

export function stripMathText(text) {
  const withoutExplicitMath = String(text || '')
    .replace(/\\\[[\s\S]*?\\\]/g, ' ')
    .replace(/\\\([\s\S]*?\\\)/g, ' ')
    .replace(/\$\$[\s\S]*?\$\$/g, ' ');

  return stripSingleDollarMath(withoutExplicitMath);
}
